import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle, Phone, Sparkles, Sticker, Users, Download,
  ShieldCheck, Wifi, ArrowRight, Zap, RefreshCw, BellRing, Moon, Sun,
  Newspaper, Apple, Play, Heart, X, Send,
} from "lucide-react";
import { Wordmark, Logo } from "@/components/Logo";
import { QRCodeSVG } from "qrcode.react";
import LangToggle from "@/components/LangToggle";
import InstallHub from "@/components/InstallHub";
import TermsDialog from "@/components/chat/TermsDialog";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import { getDeviceType } from "@/lib/device";
import { toast } from "sonner";

const HERO_BG = "https://images.unsplash.com/photo-1709377195538-5522ed0f9e10?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBhYnN0cmFjdCUyMGRhcmslMjBnb2xkfGVufDB8fHx8MTc4Njc1Nzg0NHww&ixlib=rb-4.1.0&q=85";
const PEOPLE_IMG = "https://images.unsplash.com/photo-1644043350898-2f4ff1e17912?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxBZnJpY2FuJTIwcGVvcGxlJTIwc21pbGluZyUyMHVzaW5nJTIwc21hcnRwaG9uZXN8ZW58MHx8fHwxNzg2Nzc3MDEyfDA&ixlib=rb-4.1.0&q=85";

// Diverse users of RoyalChat Nova, on their phones — shown as gold-circled avatars (matching the in-app avatar ring).
const AV = "&w=240&h=240&fit=facearea&facepad=3";
const PEOPLE = [
  { name: "Amara", img: "https://images.unsplash.com/photo-1744880034743-7d75853080ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxBZnJpY2FuJTIwcGVyc29uJTIwdXNpbmclMjBzbWFydHBob25lJTIwc21pbGluZ3xlbnwwfHx8fDE3ODg2NjQ0ODN8MA&ixlib=rb-4.1.0&q=85" + AV },
  { name: "Mei", img: "https://images.unsplash.com/photo-1677641616290-c253027cb76f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxBc2lhbiUyMHBlcnNvbiUyMHVzaW5nJTIwc21hcnRwaG9uZSUyMHNtaWxpbmd8ZW58MHx8fHwxNzg4NjY0NDg4fDA&ixlib=rb-4.1.0&q=85" + AV },
  { name: "Layla", img: "https://images.unsplash.com/photo-1649767821633-f0d6a8c28b51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxBcmFiJTIwd29tYW4lMjBoaWphYiUyMHVzaW5nJTIwc21hcnRwaG9uZSUyMHNtaWxpbmd8ZW58MHx8fHwxNzg4NjY0NDkyfDA&ixlib=rb-4.1.0&q=85" + AV },
  { name: "Kofi", img: "https://images.unsplash.com/photo-1605896163420-830698e44fb1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxBZnJpY2FuJTIwcGVyc29uJTIwdXNpbmclMjBzbWFydHBob25lJTIwc21pbGluZ3xlbnwwfHx8fDE3ODg2NjQ0ODN8MA&ixlib=rb-4.1.0&q=85" + AV },
  { name: "Jun", img: "https://images.unsplash.com/photo-1763994679849-de757e723af8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxBc2lhbiUyMHBlcnNvbiUyMHVzaW5nJTIwc21hcnRwaG9uZSUyMHNtaWxpbmd8ZW58MHx8fHwxNzg4NjY0NDg4fDA&ixlib=rb-4.1.0&q=85" + AV },
];

// Static demo posts for the Social feed showcase (mirrors real in-app post cards).
const SOCIAL_POSTS = [
  { name: "Amara", avatar: PEOPLE[0].img, img: "https://images.unsplash.com/photo-1768244016479-756ee49fe26c?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", capKey: "land_soc_p1", time: "2h", likes: 248, comments: 31,
    demoComments: [{ name: "Layla", avatar: PEOPLE[2].img, key: "land_soc_c1" }, { name: "Jun", avatar: PEOPLE[4].img, key: "land_soc_c2" }] },
  { name: "Kofi", avatar: PEOPLE[3].img, img: "https://images.unsplash.com/photo-1760008020309-bf198b142121?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", capKey: "land_soc_p2", time: "5h", likes: 512, comments: 64,
    demoComments: [{ name: "Mei", avatar: PEOPLE[1].img, key: "land_soc_c3" }, { name: "Amara", avatar: PEOPLE[0].img, key: "land_soc_c4" }] },
  { name: "Mei", avatar: PEOPLE[1].img, img: "https://images.unsplash.com/photo-1601305070998-91e1a8574e9e?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", capKey: "land_soc_p3", time: "1d", likes: 176, comments: 12,
    demoComments: [{ name: "Kofi", avatar: PEOPLE[3].img, key: "land_soc_c5" }, { name: "Layla", avatar: PEOPLE[2].img, key: "land_soc_c6" }] },
];

// Post detail modal — auto-plays a like + comment animation for landing visitors.
function PostDemo({ post, onClose }) {
  const { t } = useI18n();
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(post.likes);
  const [burst, setBurst] = useState(false);
  const [shownComments, setShownComments] = useState(0);

  const doLike = () => {
    if (liked) { setLiked(false); setLikes((n) => n - 1); }
    else { setLiked(true); setLikes((n) => n + 1); setBurst(true); setTimeout(() => setBurst(false), 900); }
  };

  useEffect(() => {
    const timers = [];
    timers.push(setTimeout(() => { setLiked(true); setLikes((n) => n + 1); setBurst(true); }, 700));
    timers.push(setTimeout(() => setBurst(false), 1600));
    timers.push(setTimeout(() => setShownComments(1), 1500));
    timers.push(setTimeout(() => setShownComments(2), 2400));
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="post-demo-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.94, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.25 }}
        className="relative w-full max-w-md max-h-[90vh] overflow-y-auto no-scrollbar rounded-3xl border border-[var(--rc-gold)]/25 bg-[var(--rc-bg)]">
        <div className="flex items-center gap-3 p-4">
          <span className="shrink-0 h-11 w-11 rounded-full p-[2px] bg-[var(--rc-gold)]">
            <img src={post.avatar} alt={post.name} className="h-full w-full rounded-full object-cover border-2 border-[var(--rc-bg)]" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">{post.name}</div>
            <div className="text-xs text-[var(--rc-muted)]">{post.time}</div>
          </div>
          <button onClick={onClose} data-testid="post-demo-close"
            className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-muted)] hover:bg-[var(--rc-surface)] transition-colors"><X size={18} /></button>
        </div>

        <div className="relative aspect-square bg-[var(--rc-surface-2)]" onDoubleClick={doLike}>
          <img src={post.img} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <AnimatePresence>
            {burst && (
              <motion.div key="burst" initial={{ opacity: 0, scale: 0.3 }} animate={{ opacity: [0, 1, 1, 0], scale: [0.3, 1.2, 1, 1.05] }}
                exit={{ opacity: 0 }} transition={{ duration: 0.9 }}
                className="absolute inset-0 grid place-items-center pointer-events-none">
                <Heart size={110} className="text-white drop-shadow-xl" fill="#ef4444" strokeWidth={0} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="p-4">
          <div className="flex items-center gap-5 mb-3">
            <button onClick={doLike} data-testid="post-demo-like-btn" className="flex items-center gap-1.5 text-sm text-[var(--rc-silver)]">
              <motion.span animate={liked ? { scale: [1, 1.4, 1] } : { scale: 1 }} transition={{ duration: 0.35 }}>
                <Heart size={22} className={liked ? "text-red-500" : "text-[var(--rc-gold)]"} fill={liked ? "#ef4444" : "none"} strokeWidth={liked ? 0 : 2} />
              </motion.span>
              <motion.span key={likes} initial={{ y: -6, opacity: 0 }} animate={{ y: 0, opacity: 1 }} data-testid="post-demo-like-count">{likes}</motion.span>
            </button>
            <span className="flex items-center gap-1.5 text-sm text-[var(--rc-silver)]">
              <MessageCircle size={22} className="text-[var(--rc-gold)]" /> {post.comments}
            </span>
          </div>
          <p className="text-sm leading-relaxed mb-4">
            <span className="font-semibold">{post.name}</span>{" "}
            <span className="text-[var(--rc-muted)]">{t(post.capKey)}</span>
          </p>

          <div className="space-y-3" data-testid="post-demo-comments">
            <AnimatePresence>
              {post.demoComments.slice(0, shownComments).map((c) => (
                <motion.div key={c.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
                  className="flex items-start gap-2.5">
                  <span className="shrink-0 h-8 w-8 rounded-full p-[1.5px] bg-[var(--rc-gold)]">
                    <img src={c.avatar} alt={c.name} className="h-full w-full rounded-full object-cover border-2 border-[var(--rc-bg)]" />
                  </span>
                  <div className="rounded-2xl rounded-tl-sm bg-[var(--rc-surface)] border border-[var(--rc-border)] px-3.5 py-2 text-sm">
                    <span className="font-semibold mr-1.5">{c.name}</span>
                    <span className="text-[var(--rc-muted)]">{t(c.key)}</span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-full border border-[var(--rc-border)] bg-[var(--rc-surface)] px-4 py-2.5 opacity-80">
            <span className="text-sm text-[var(--rc-muted)] flex-1">{t("land_soc_add_comment")}</span>
            <Send size={16} className="text-[var(--rc-gold)]" />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

const FEATURES = [
  { icon: Sparkles, tKey: "land_f1_t", dKey: "land_f1_d", span: "lg:col-span-7" },
  { icon: Phone, tKey: "land_f2_t", dKey: "land_f2_d", span: "lg:col-span-5" },
  { icon: Sparkles, tKey: "land_f3_t", dKey: "land_f3_d", span: "lg:col-span-5" },
  { icon: Sticker, tKey: "land_f4_t", dKey: "land_f4_d", span: "lg:col-span-7" },
  { icon: Users, tKey: "land_f5_t", dKey: "land_f5_d", span: "lg:col-span-12" },
];

const STEPS = [
  { icon: Download, tKey: "land_s1_t", dKey: "land_s1_d" },
  { icon: MessageCircle, tKey: "land_s2_t", dKey: "land_s2_d" },
  { icon: Sparkles, tKey: "land_s3_t", dKey: "land_s3_d" },
];

function Section({ children, className = "", id }) {
  return <section id={id} className={`px-5 sm:px-8 lg:px-16 ${className}`}>{children}</section>;
}

// Showcase feed card that gently auto-cycles a like/comment "beat" so the feed feels alive.
function LivePost({ post, index, onOpen }) {
  const { t } = useI18n();
  const [likes, setLikes] = useState(post.likes);
  const [pulse, setPulse] = useState(false);
  const [liveComment, setLiveComment] = useState(null);
  const beatRef = useRef(0);

  useEffect(() => {
    let commentTimer, pulseTimer;
    const beat = () => {
      setLikes((n) => n + 1);
      setPulse(true);
      pulseTimer = setTimeout(() => setPulse(false), 800);
      const c = post.demoComments[beatRef.current % post.demoComments.length];
      beatRef.current += 1;
      setLiveComment(c);
      commentTimer = setTimeout(() => setLiveComment(null), 3200);
    };
    const start = setTimeout(() => { beat(); }, 1200 + index * 1600);
    const interval = setInterval(beat, 6500);
    return () => { clearTimeout(start); clearInterval(interval); clearTimeout(commentTimer); clearTimeout(pulseTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ delay: index * 0.08, duration: 0.5 }}
      data-testid={`social-post-${index}`}
      onClick={onOpen}
      className="group cursor-pointer rounded-3xl border border-[var(--rc-border)] bg-[var(--rc-surface)] overflow-hidden hover:-translate-y-1 hover:border-[var(--rc-gold)]/40 transition-all">
      <div className="flex items-center gap-3 p-4">
        <span className="shrink-0 h-11 w-11 rounded-full p-[2px] bg-[var(--rc-gold)]">
          <img src={post.avatar} alt={post.name} loading="lazy" className="h-full w-full rounded-full object-cover border-2 border-[var(--rc-surface)]" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{post.name}</div>
          <div className="text-xs text-[var(--rc-muted)]">{post.time}</div>
        </div>
      </div>
      <div className="relative aspect-square bg-[var(--rc-surface-2)] overflow-hidden">
        <img src={post.img} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors grid place-items-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1.5 rounded-full bg-[var(--rc-gold)] text-black text-xs font-semibold px-3 py-1.5">
            <Heart size={13} fill="currentColor" strokeWidth={0} /> {t("land_soc_tap")}
          </span>
        </div>
        {/* live floating comment */}
        <AnimatePresence>
          {liveComment && (
            <motion.div key={liveComment.key + beatRef.current}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }} data-testid={`social-post-${index}-live-comment`}
              className="absolute left-3 right-3 bottom-3 flex items-center gap-2 rounded-2xl bg-black/55 backdrop-blur-md px-3 py-2">
              <span className="shrink-0 h-7 w-7 rounded-full p-[1.5px] bg-[var(--rc-gold)]">
                <img src={liveComment.avatar} alt="" className="h-full w-full rounded-full object-cover border border-black" />
              </span>
              <span className="text-xs text-white truncate">
                <span className="font-semibold mr-1">{liveComment.name}</span>{t(liveComment.key)}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-5 mb-3">
          <span className="flex items-center gap-1.5 text-sm text-[var(--rc-silver)]">
            <motion.span animate={pulse ? { scale: [1, 1.45, 1] } : { scale: 1 }} transition={{ duration: 0.6 }}>
              <Heart size={18} className={pulse ? "text-red-500" : "text-[var(--rc-gold)]"} fill={pulse ? "#ef4444" : "var(--rc-gold)"} strokeWidth={0} />
            </motion.span>
            <motion.span key={likes} initial={{ y: -5, opacity: 0.4 }} animate={{ y: 0, opacity: 1 }} data-testid={`social-post-${index}-likes`}>{likes}</motion.span>
          </span>
          <span className="flex items-center gap-1.5 text-sm text-[var(--rc-silver)]">
            <MessageCircle size={18} className="text-[var(--rc-gold)]" /> {post.comments}
          </span>
        </div>
        <p className="text-sm leading-relaxed">
          <span className="font-semibold">{post.name}</span>{" "}
          <span className="text-[var(--rc-muted)]">{t(post.capKey)}</span>
        </p>
      </div>
    </motion.div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const { t } = useI18n();
  const [termsOpen, setTermsOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [activePost, setActivePost] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    setDevice(getDeviceType());
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isDesktop = device === "desktop";

  const openApp = () => navigate(user ? "/app" : "/login");
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "https://royalchat.online";

  const handleInstall = () => setInstallOpen(true);

  return (
    <div className="min-h-screen bg-[var(--rc-bg)] text-[var(--rc-text)] overflow-x-hidden">
      {/* Nav */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-[var(--rc-bg)]/80 backdrop-blur-xl border-b border-[var(--rc-border)]" : ""}`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 h-16 flex items-center justify-between">
          <Wordmark size={34} />
          <div className="flex items-center gap-2 sm:gap-3">
            <LangToggle />
            <button onClick={toggle} data-testid="landing-theme-toggle"
              className="h-10 w-10 grid place-items-center rounded-full border border-[var(--rc-border)] text-[var(--rc-silver)] hover:text-[var(--rc-gold)] transition-colors">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="relative min-h-[92vh] flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-[var(--rc-bg)]" style={{ opacity: theme === "dark" ? 0.62 : 0.82 }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 20%, rgba(212,175,55,0.15), transparent 55%)" }} />
        </div>
        <Section className="relative z-10 w-full pt-24">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
              className="flex justify-center mb-8">
              <Logo size={92} />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-[var(--rc-gold)]/30 bg-[var(--rc-gold)]/10 px-4 py-1.5 text-sm text-[var(--rc-gold)] mb-6">
              <ShieldCheck size={15} /> {t("land_badge")}
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}
              className="font-heading font-semibold text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
              {t("land_hero_1")} <span className="text-[var(--rc-gold)]">{t("land_hero_accent")}</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
              className="mt-6 text-lg text-[var(--rc-muted)] max-w-2xl mx-auto leading-relaxed">
              {t("land_hero_sub")}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }}
              className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-5">
              {isDesktop && (
                <div data-testid="hero-qr" className="flex items-center gap-4 rounded-3xl border border-[var(--rc-gold)]/25 bg-[var(--rc-surface)] px-5 py-4">
                  <div className="rounded-2xl bg-white p-2.5 shadow-lg shrink-0">
                    <QRCodeSVG value={siteUrl} size={104} level="M" includeMargin={false} />
                  </div>
                  <div className="text-left max-w-[180px]">
                    <div className="font-heading text-lg leading-tight">{t("land_qr_title")}</div>
                    <p className="mt-1 text-sm text-[var(--rc-muted)] leading-snug">{t("land_qr_sub")}</p>
                  </div>
                </div>
              )}
              <button onClick={handleInstall} data-testid="hero-install-btn"
                className={isDesktop
                  ? "w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-[var(--rc-gold)] text-[var(--rc-gold)] font-semibold px-8 py-4 text-lg hover:bg-[var(--rc-gold)]/10 transition-colors"
                  : "w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold px-8 py-4 text-lg hover:bg-[var(--rc-gold-light)] hover:-translate-y-0.5 transition-transform"}
                style={!isDesktop ? { boxShadow: "0 0 30px rgba(212,175,55,0.25)" } : undefined}>
                <Download size={18} /> {device === "ios" ? t("install") : t("download_royalchat")}
              </button>
            </motion.div>

            {/* App store buttons — coming soon (not clickable) */}
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.7 }}
              className="mt-8 flex flex-col items-center gap-3" data-testid="store-buttons">
              <span className="text-xs uppercase tracking-widest text-[var(--rc-muted)]">{t("land_native_apps")}</span>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                {[["App Store", Apple, "apple"], ["Google Play", Play, "play"]].map(([label, Icon, key]) => (
                  <div key={key} data-testid={`store-btn-${key}`} aria-disabled="true"
                    className="relative flex items-center gap-3 rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-surface)] px-5 py-3 opacity-70 cursor-not-allowed select-none">
                    <Icon size={26} className="text-[var(--rc-silver)]" />
                    <div className="text-left leading-tight">
                      <div className="text-[10px] text-[var(--rc-muted)]">{t("land_download_on")}</div>
                      <div className="font-semibold">{label}</div>
                    </div>
                    <span className="absolute -top-2 -right-2 rotate-6 rounded-full bg-[var(--rc-gold)] text-black text-[10px] font-extrabold px-2 py-0.5 shadow">{t("land_coming_soon")}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </Section>
      </div>

      {/* Three pillars: Chat · Social · Marketplace */}
      <Section className="py-20 lg:py-28 max-w-7xl mx-auto" id="pillars">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-[var(--rc-gold)] font-medium mb-3 tracking-wide">{t("land_pillars_label")}</p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight">{t("land_pillars_title")}</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-testid="landing-pillars">
          {[
            { icon: MessageCircle, title: t("land_p1_t"), desc: t("land_p1_d"), chips: [t("land_p1_c1"), t("land_p1_c2"), t("land_p1_c3")] },
            { icon: Newspaper, title: t("land_p2_t"), desc: t("land_p2_d"), chips: [t("land_p2_c1"), t("land_p2_c2"), t("land_p2_c3")] },
          ].map((p, i) => (
            <motion.div key={p.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="group rounded-3xl border border-[var(--rc-border)] bg-[var(--rc-surface)] p-8 hover:-translate-y-1 transition-transform">
              <div className="h-14 w-14 rounded-2xl grid place-items-center bg-[var(--rc-gold)]/10 border border-[var(--rc-gold)]/20 text-[var(--rc-gold)] mb-5">
                <p.icon size={26} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl mb-2">{p.title}</h3>
              <p className="text-[var(--rc-muted)] leading-relaxed">{p.desc}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {p.chips.map((c) => <span key={c} className="rounded-full border border-[var(--rc-border)] px-3 py-1 text-xs text-[var(--rc-silver)]">{c}</span>)}
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Capabilities bento */}
      <Section id="features" className="py-24 lg:py-32 max-w-7xl mx-auto">
        <div className="max-w-2xl mb-14">
          <p className="text-[var(--rc-gold)] font-medium mb-3 tracking-wide">{t("land_cap_label")}</p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight">{t("land_cap_title")}</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div key={f.tKey}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.5 }}
              className={`group relative overflow-hidden rounded-3xl border border-[var(--rc-border)] bg-[var(--rc-surface)] p-8 hover:-translate-y-1 transition-transform ${f.span}`}>
              <div className="h-12 w-12 rounded-2xl grid place-items-center bg-[var(--rc-gold)]/10 border border-[var(--rc-gold)]/20 text-[var(--rc-gold)] mb-5">
                <f.icon size={22} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl mb-2">{t(f.tKey)}</h3>
              <p className="text-[var(--rc-muted)] leading-relaxed">{t(f.dKey)}</p>
              <div className="absolute -right-8 -bottom-8 h-32 w-32 rounded-full bg-[var(--rc-gold)]/5 blur-2xl group-hover:bg-[var(--rc-gold)]/10 transition-colors" />
            </motion.div>
          ))}
        </div>
      </Section>

      {/* How it works */}
      <Section className="py-24 lg:py-32 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[var(--rc-gold)] font-medium mb-3 tracking-wide">{t("land_how_label")}</p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight mb-10">{t("land_how_title")}</h2>
            <div className="space-y-8">
              {STEPS.map((s, i) => (
                <div key={s.tKey} className="flex gap-5">
                  <div className="shrink-0 h-12 w-12 rounded-full grid place-items-center bg-[var(--rc-gold)]/10 border border-[var(--rc-gold)]/25 text-[var(--rc-gold)] font-heading font-semibold">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl mb-1 flex items-center gap-2">
                      <s.icon size={18} className="text-[var(--rc-gold)]" /> {t(s.tKey)}
                    </h3>
                    <p className="text-[var(--rc-muted)] leading-relaxed">{t(s.dKey)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative rounded-3xl overflow-hidden border border-[var(--rc-border)]">
            <img src={PEOPLE_IMG} alt="People chatting on their phones" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--rc-bg)] via-[var(--rc-bg)]/20 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex flex-col gap-4">
              <div className="flex items-center gap-3 rounded-2xl bg-[var(--rc-bg)]/70 backdrop-blur-xl border border-[var(--rc-border)] px-4 py-3 self-start">
                <Zap className="text-[var(--rc-gold)]" size={20} />
                <span className="text-sm">{t("land_crafted")}</span>
              </div>
              <div className="flex items-center gap-3 rounded-2xl bg-[var(--rc-bg)]/70 backdrop-blur-xl border border-[var(--rc-border)] px-4 py-3">
                <div className="flex -space-x-3" data-testid="landing-people-avatars">
                  {PEOPLE.map((p) => (
                    <span key={p.name} className="h-11 w-11 rounded-full p-[2px] bg-[var(--rc-gold)] shadow-md" title={p.name}>
                      <img src={p.img} alt={p.name} loading="lazy"
                        className="h-full w-full rounded-full object-cover border-2 border-[var(--rc-bg)]" />
                    </span>
                  ))}
                </div>
                <span className="text-sm leading-tight text-[var(--rc-silver)]">{t("land_global")}</span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Mockups */}
      <Section className="py-24 lg:py-32 max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-[var(--rc-gold)] font-medium mb-3 tracking-wide">{t("land_inside_label")}</p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight">{t("land_inside_title")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { title: t("land_mock_chats"), items: [["Amara", t("land_mock_talk_soon"), true, PEOPLE[0].img], [t("land_mock_design"), t("land_mock_design_msg"), false, PEOPLE[1].img], [t("land_mock_mother"), t("land_mock_mother_msg"), false, PEOPLE[2].img]] },
            { title: t("land_mock_convo"), items: null },
            { title: t("land_mock_ai"), items: null },
          ].map((m, idx) => (
            <div key={m.title} className="rounded-[2rem] border border-[var(--rc-border)] bg-[var(--rc-surface)] p-4 aspect-[9/16] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-2 py-3 border-b border-[var(--rc-border)]">
                <span className="font-heading text-xl">{m.title}</span>
                <MessageCircle size={16} className="text-[var(--rc-gold)]" />
              </div>
              {idx === 0 && (
                <div className="flex-1 space-y-1 pt-3">
                  {m.items.map(([n, p, pin, img]) => (
                    <div key={n} className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-[var(--rc-surface-2)]">
                      <span className="shrink-0 h-10 w-10 rounded-full p-[2px] bg-[var(--rc-gold)]">
                        <img src={img} alt={n} loading="lazy" className="h-full w-full rounded-full object-cover border-2 border-[var(--rc-surface)]" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-sm font-medium">{n}{pin && <span className="text-[var(--rc-gold)] text-xs">📌</span>}</div>
                        <div className="text-xs text-[var(--rc-muted)] truncate">{p}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {idx === 1 && (
                <div className="flex-1 flex flex-col justify-end gap-2 py-4">
                  <div className="self-start max-w-[75%] rounded-2xl rounded-bl-sm bg-[var(--rc-bubble-in)] border border-[var(--rc-border)] px-4 py-2 text-sm">{t("land_mock_msg1")}</div>
                  <div className="self-end max-w-[75%] rounded-2xl rounded-br-sm bg-[var(--rc-gold)]/10 border border-[var(--rc-gold)]/25 px-4 py-2 text-sm">{t("land_mock_msg2")}</div>
                  <div className="self-start flex gap-1 px-3 py-2">
                    <span className="h-2 w-2 rounded-full bg-[var(--rc-gold)] rc-dot" />
                    <span className="h-2 w-2 rounded-full bg-[var(--rc-gold)] rc-dot" style={{ animationDelay: "0.2s" }} />
                    <span className="h-2 w-2 rounded-full bg-[var(--rc-gold)] rc-dot" style={{ animationDelay: "0.4s" }} />
                  </div>
                </div>
              )}
              {idx === 2 && (
                <div className="flex-1 flex flex-col justify-center items-center text-center gap-3 px-3">
                  <Sparkles className="text-[var(--rc-gold)]" size={40} />
                  <p className="text-sm text-[var(--rc-muted)]">{t("land_mock_ai_desc")}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Social feed showcase */}
        <div className="mt-20" data-testid="landing-social-showcase">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-[var(--rc-gold)] font-medium mb-3 tracking-wide">{t("land_soc_label")}</p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight">{t("land_soc_title")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SOCIAL_POSTS.map((post, i) => (
              <LivePost key={post.name} post={post} index={i} onOpen={() => setActivePost(post)} />
            ))}
          </div>
        </div>
      </Section>
      <Section id="install" className="py-24 max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-[var(--rc-gold)] font-medium mb-3 tracking-wide">{t("land_nav_install")}</p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight mb-3">{t("land_install_title")}</h2>
          <p className="text-[var(--rc-muted)]">{t("land_install_sub")}</p>
        </div>
        <InstallHub openApp={openApp} />
      </Section>

      {/* Footer */}
      <footer className="border-t border-[var(--rc-border)] py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 flex flex-col sm:flex-row items-center justify-between gap-6">
          <Wordmark size={30} />
          <div className="flex items-center gap-6 text-sm text-[var(--rc-muted)]">
            <a href="#features" className="hover:text-[var(--rc-gold)] transition-colors">{t("land_nav_features")}</a>
            <a href="#install" className="hover:text-[var(--rc-gold)] transition-colors">{t("land_nav_install")}</a>
            <button onClick={() => setTermsOpen(true)} className="hover:text-[var(--rc-gold)] transition-colors" data-testid="footer-terms">{t("land_terms")}</button>
            <a href="/privacy" className="hover:text-[var(--rc-gold)] transition-colors" data-testid="footer-privacy">{t("land_privacy")}</a>
          </div>
          <div className="flex items-center gap-2 text-xs text-[var(--rc-muted)]">
            <BellRing size={14} className="text-[var(--rc-gold)]" /> © {new Date().getFullYear()} RoyalChat Nova
          </div>
        </div>
      </footer>
      {installOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="install-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setInstallOpen(false)} />
          <motion.div initial={{ opacity: 0, scale: 0.94, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.25 }}
            className="relative w-full max-w-md max-h-[88vh] overflow-y-auto rounded-3xl border border-[var(--rc-gold)]/25 bg-[var(--rc-bg)] p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Logo size={28} />
                <span className="font-heading text-lg">{t("land_install_title")}</span>
              </div>
              <button onClick={() => setInstallOpen(false)} data-testid="install-modal-close"
                className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-muted)] hover:bg-[var(--rc-surface)] transition-colors"><X size={18} /></button>
            </div>
            <InstallHub autoStart openApp={() => { setInstallOpen(false); openApp(); }} />
          </motion.div>
        </div>
      )}
      {termsOpen && <TermsDialog onClose={() => setTermsOpen(false)} />}
      <AnimatePresence>
        {activePost && <PostDemo key="post-demo" post={activePost} onClose={() => setActivePost(null)} />}
      </AnimatePresence>
    </div>
  );
}
